import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTicketWaiver, type TicketWaiverType, type WaiverCollectionMode } from '@/lib/api/tickets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { createWaiverDisplayToken, getDisplayConnectionStatus } from '@/lib/api/display-staff.js';
import { DISPLAY_STAFF_WS_EVENT } from '@/hooks/useDisplayStaffBridge.js';

type CollectionTab = 'hand_across' | 'customer_display';

type Phase = 'confirm' | 'review' | 'sign';

export interface WaiverSignResult {
  signatureDataUrl: string;
  waiverVersion: string;
  collectionMode: WaiverCollectionMode;
}

function tabFromDefault(mode: WaiverCollectionMode): CollectionTab {
  return mode === 'customer_display' ? 'customer_display' : 'hand_across';
}

export function WaiverModal({
  open,
  onClose,
  ticketId,
  waiverType,
  defaultCollectionMode,
  allowSkip,
  onSkip,
  onSigned,
  onDisplaySignatureComplete,
}: {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  waiverType: TicketWaiverType;
  defaultCollectionMode: WaiverCollectionMode;
  allowSkip?: boolean;
  onSkip?: () => void;
  onSigned: (result: WaiverSignResult) => void | Promise<void>;
  onDisplaySignatureComplete?: () => void | Promise<void>;
}) {
  const storeId = useAuthStore((s) => s.user?.storeId);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<CollectionTab>(() => tabFromDefault(defaultCollectionMode));
  const [phase, setPhase] = useState<Phase>('confirm');
  const [submitting, setSubmitting] = useState(false);
  const [sendingDisplay, setSendingDisplay] = useState(false);
  const [waitingDisplaySig, setWaitingDisplaySig] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const hasInk = useRef(false);

  const waiverQuery = useQuery({
    queryKey: ['ticket-waiver', ticketId, waiverType],
    queryFn: () => getTicketWaiver(ticketId, waiverType),
    enabled: open && Boolean(ticketId),
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    setTab(tabFromDefault(defaultCollectionMode));
    setPhase('confirm');
    setSubmitting(false);
    setSendingDisplay(false);
    setWaitingDisplaySig(false);
    hasInk.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [open, defaultCollectionMode, ticketId, waiverType]);

  useEffect(() => {
    if (waiverQuery.isError) {
      const parsed = parseApiError(waiverQuery.error);
      toastError(parsed.message, parsed.code);
    }
  }, [waiverQuery.isError, waiverQuery.error]);

  useEffect(() => {
    if (!open || !waitingDisplaySig) return;
    const handler = (ev: Event) => {
      const msg = (ev as CustomEvent<{ type?: string; payload?: { ticketId?: string; waiverType?: string } }>)
        .detail;
      if (msg?.type !== 'display.sig_received') return;
      const p = msg.payload;
      if (p?.ticketId !== ticketId || p?.waiverType !== waiverType) return;
      setWaitingDisplaySig(false);
      void (async () => {
        toastSuccess('Signature received! Customer display signed the waiver. ✓');
        if (onDisplaySignatureComplete) {
          await onDisplaySignatureComplete();
        } else {
          await queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
        }
        onClose();
      })();
    };
    window.addEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
    return () => window.removeEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
  }, [
    open,
    waitingDisplaySig,
    ticketId,
    waiverType,
    onClose,
    onDisplaySignatureComplete,
    queryClient,
  ]);

  const pos = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    if ('clientX' in e) {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }
    return null;
  }, []);

  const startDraw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const p = pos(e);
      if (!ctx || !p) return;
      drawing.current = true;
      last.current = p;
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    },
    [pos],
  );

  const draw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!drawing.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const p = pos(e);
      if (!ctx || !p || !last.current) return;
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      hasInk.current = true;
      last.current = p;
    },
    [pos],
  );

  const endDraw = useCallback(() => {
    drawing.current = false;
    last.current = null;
  }, []);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
  }, []);

  async function sendWaiverToDisplay() {
    if (!storeId) {
      toastError('No store selected.');
      return;
    }
    setSendingDisplay(true);
    try {
      const status = await getDisplayConnectionStatus(storeId);
      if (!status.connected) {
        toastError('Customer display not connected. Use hand-across mode.');
        return;
      }
      await createWaiverDisplayToken(ticketId, { waiverType });
      setWaitingDisplaySig(true);
      toastSuccess('Waiting for customer signature on display…');
    } catch (err) {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    } finally {
      setSendingDisplay(false);
    }
  }

  async function submitSignature() {
    const canvas = canvasRef.current;
    const version = waiverQuery.data?.version;
    if (!canvas || !version) {
      toastError('Waiver is still loading.');
      return;
    }
    let dataUrl: string;
    try {
      dataUrl = canvas.toDataURL('image/png');
    } catch {
      toastError('Could not read signature from canvas.');
      return;
    }
    if (!hasInk.current) {
      toastError('Please sign in the box before submitting.');
      return;
    }
    const collectionMode: WaiverCollectionMode =
      tab === 'customer_display' ? 'customer_display' : 'hand_across';
    setSubmitting(true);
    try {
      await onSigned({ signatureDataUrl: dataUrl, waiverVersion: version, collectionMode });
      onClose();
    } catch (err) {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const title = waiverType === 'pre_repair' ? 'Pre-repair waiver' : 'Post-repair waiver';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-950">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">
            Customer authorization. Choose how the waiver is presented.
          </p>
          <div className="mt-4 flex rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              className={[
                'flex-1 rounded-lg px-3 py-2 text-sm font-semibold',
                tab === 'hand_across' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600',
              ].join(' ')}
              onClick={() => {
                setTab('hand_across');
                setPhase('confirm');
              }}
            >
              Hand across (this device)
            </button>
            <button
              type="button"
              className={[
                'flex-1 rounded-lg px-3 py-2 text-sm font-semibold',
                tab === 'customer_display' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600',
              ].join(' ')}
              onClick={() => setTab('customer_display')}
            >
              Customer display
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {tab === 'customer_display' ? (
            <div className="space-y-4">
              {waitingDisplaySig ? (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 py-10 text-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <p className="text-sm font-semibold text-slate-800">Waiting for customer signature on display…</p>
                  <p className="max-w-md text-sm text-slate-600">
                    The customer completes the waiver on the facing iPad. This window closes automatically when the
                    signature is received.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-700">
                    Send the waiver and signature pad to your customer-facing display. Ensure the display page is open
                    at <span className="font-mono text-xs">/display/{storeId?.slice(0, 8)}…</span>
                  </p>
                  <button
                    type="button"
                    disabled={sendingDisplay || !waiverQuery.data}
                    className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                    onClick={() => void sendWaiverToDisplay()}
                  >
                    {sendingDisplay ? 'Sending…' : 'Send to customer display'}
                  </button>
                </>
              )}
            </div>
          ) : waiverQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading waiver…</p>
          ) : waiverQuery.data ? (
            <>
              {phase === 'confirm' ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-700">
                    Confirm the customer is ready to read and sign the {title.toLowerCase()} on this
                    device. Hand the tablet to the customer for the next steps.
                  </p>
                  <ul className="list-inside list-disc text-sm text-slate-600">
                    <li>Verify customer identity matches the ticket.</li>
                    <li>Customer should read the full waiver before signing.</li>
                  </ul>
                </div>
              ) : null}

              {phase === 'review' ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Waiver text (v{waiverQuery.data.version})
                  </p>
                  <div className="max-h-[42vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">
                    {waiverQuery.data.content}
                  </div>
                </div>
              ) : null}

              {phase === 'sign' ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Sign below with finger or stylus. Use <strong>Clear</strong> to start over.
                  </p>
                  <canvas
                    ref={canvasRef}
                    width={900}
                    height={260}
                    className="w-full touch-none rounded-xl border-2 border-slate-300 bg-white"
                    style={{ maxHeight: '220px' }}
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      startDraw(e);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      draw(e);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      endDraw();
                    }}
                  />
                  <button
                    type="button"
                    className="text-sm font-semibold text-primary"
                    onClick={clearSignature}
                  >
                    Clear signature
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-red-600">Could not load waiver.</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          {allowSkip && onSkip && phase === 'confirm' && waiverType === 'pre_repair' ? (
            <button
              type="button"
              className="mr-auto rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              onClick={() => {
                onSkip();
                onClose();
              }}
            >
              Skip waiver
            </button>
          ) : null}
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
            onClick={onClose}
          >
            Cancel
          </button>
          {tab === 'hand_across' && waiverQuery.data ? (
            <>
              {phase === 'confirm' ? (
                <button
                  type="button"
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                  onClick={() => setPhase('review')}
                >
                  Continue
                </button>
              ) : null}
              {phase === 'review' ? (
                <button
                  type="button"
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                  onClick={() => {
                    setPhase('sign');
                    requestAnimationFrame(() => {
                      const canvas = canvasRef.current;
                      const ctx = canvas?.getContext('2d');
                      if (canvas && ctx) {
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        hasInk.current = false;
                      }
                    });
                  }}
                >
                  Customer has read — sign
                </button>
              ) : null}
              {phase === 'sign' ? (
                <button
                  type="button"
                  disabled={submitting}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  onClick={() => void submitSignature()}
                >
                  {submitting ? 'Saving…' : 'Submit signature'}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
